
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useCreateDevice } from "@/hooks/useDevices";
import { Lock } from "lucide-react";
import { Room } from "@/hooks/useRooms";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  device_name: z.string().min(1, "Device name is required"),
  device_id: z.string()
    .min(1, "Device ID is required")
    .refine(
      (val) => {
        // Allow both UUID format and other device ID formats
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const alphanumericRegex = /^[a-zA-Z0-9\-_]+$/;
        return uuidRegex.test(val) || alphanumericRegex.test(val);
      },
      "Device ID must be in UUID format or alphanumeric with dashes/underscores"
    ),
  device_pin: z.string().optional(),
  seam_device_id: z.string().optional(),
});

interface AddDeviceDialogProps {
  providerId: string;
  room: Room;
  onDeviceAdded: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const AddDeviceDialog = ({ 
  providerId, 
  room, 
  onDeviceAdded, 
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange 
}: AddDeviceDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const createDevice = useCreateDevice();
  const { toast } = useToast();

  // Use controlled open state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      device_name: "",
      device_id: "",
      device_pin: "",
      seam_device_id: "",
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        device_name: "",
        device_id: "",
        device_pin: "",
        seam_device_id: "",
      });
    }
  }, [open, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    console.log('AddDeviceDialog: Submitting device creation with values:', values);
    console.log('AddDeviceDialog: Provider ID:', providerId);
    console.log('AddDeviceDialog: Room ID:', room.id);
    
    try {
      const deviceData = {
        provider_id: providerId,
        room_id: room.id,
        device_name: values.device_name,
        device_id: values.device_id,
        device_pin: values.device_pin || undefined,
        seam_device_id: values.seam_device_id || undefined,
        is_active: true,
        is_enabled: true, // Add the missing is_enabled property
      };
      
      console.log('AddDeviceDialog: Creating device with data:', deviceData);
      
      await createDevice.mutateAsync(deviceData);

      form.reset();
      setOpen(false);
      onDeviceAdded();
      
      toast({
        title: "Success",
        description: `Device "${values.device_name}" has been added successfully to ${room.name}.`,
      });
      
    } catch (error) {
      console.error('AddDeviceDialog: Error creating device:', error);
      
      // More detailed error handling
      let errorMessage = "Failed to create device. Please try again.";
      
      if (error instanceof Error) {
        if (error.message.includes('unique')) {
          errorMessage = "A device with this ID already exists. Please use a different Device ID.";
        } else if (error.message.includes('foreign key')) {
          errorMessage = "Invalid room or provider. Please refresh the page and try again.";
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlledOpen && (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Lock className="h-4 w-4 mr-2" />
          Add Device
        </Button>
      )}
      
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Access Device to {room.name}</DialogTitle>
          <DialogDescription>
            Configure a door access control device for {room.name}. This will enable self-service entry for customers.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="device_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Device Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Front Door Lock, Room Access Control" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="device_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Device ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Internal device identifier" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seam_device_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Seam Device ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Device ID from your Seam account (required for access codes)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="device_pin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Device PIN (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Access PIN if required" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={createDevice.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createDevice.isPending}>
                {createDevice.isPending ? "Adding..." : "Add Device"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddDeviceDialog;
